import { buildUrlFromContext } from '@/lib/url.server';
import { generateContentSchema } from '@/utils/content-schema';
import { generatePageDesignerLists } from '@/utils/page-designer-schema.server';
import { getSchemaSite } from '@/lib/schema-site.server';
import { resolvePageSeo } from '@/lib/page-seo';
import { Suspense, use, useCallback, useEffect, useMemo, useRef, useState, useTransition } from 'react';
import {
    redirect,
    Await,
    type LoaderFunctionArgs,
    type ShouldRevalidateFunctionArgs,
    useLocation,
    useNavigation,
    type MetaDescriptor,
} from 'react-router';
import { ApiError, type ShopperProducts, type ShopperSearch } from '@/scapi';
import { NormalizedApiError } from '@/lib/api/normalized-api-error';
import { fetchCategory } from '@/lib/api/categories.server';
import { fetchSearchProducts } from '@/lib/api/search.server';
import { getAllQueryParams, getQueryParam, PRODUCT_SEARCH_QUERY_PARAMS } from '@/lib/query-params';
import { getConfig, useConfig } from '@salesforce/storefront-next-runtime/config';
import type { AppConfig } from '@/types/config';
import { siteContext, type SiteContext } from '@salesforce/storefront-next-runtime/site-context';
import { type EatalyBreadcrumbItem } from '@/components/eataly-breadcrumbs';
import CustomPagination from '@/components/custom-pagination';
import ProductGrid from '@/components/product-grid';
import { useAnalytics } from '@/hooks/use-analytics';
import { AttributeDefinition, PageType, RegionDefinition } from '@/lib/decorators';
import { Region } from '@/components/region';
import { fetchPageWithComponentData, type Page, type PageWithComponentData } from '@/lib/util/pageLoader.server';
import { notFoundResponse } from '@/lib/not-found.server';
import { JsonLd } from '@/components/json-ld';
import { DEFAULT_SITE_NAME_KEY } from '@/components/seo-meta';
import { UITarget } from '@/targets/ui-target';
import { generateCategorySchema } from '@/utils/category-schema';
import { getPublicOrigin } from '@/utils/schema-url';
import { buildCanonicalUrl } from '@/utils/canonical-url';
import { isCategoryPlpPathname, normalizeCategoryPath } from '@/lib/category-url';
import {
    getInitialFiltersOpen,
    getSearchWithoutClientOnlyParams,
    isSamePathnameListingDataNavigationPending,
    useFiltersPanelState,
} from '@/hooks/use-filters-panel-state';
import { getInitialOrdinationOpen, useOrdinationPanelState } from '@/hooks/use-ordination-panel-state';
import { useListingPaginationScroll } from '@/hooks/use-listing-pagination-scroll';
import { getLogger } from '@/lib/logger.server';
import { shouldSkipRevalidationForCartOrWishlistAndNewsletterActions } from '@/lib/should-revalidate';
import { authContext } from '@/middlewares/auth.utils';
import { createApiClients } from '@/lib/api-clients.server';
import { buildEatalyCategoryBreadcrumbItems } from '@/lib/breadcrumb/build-eataly-breadcrumb-items';
import { resolveBreadcrumbMergeHomeWithFirstItem } from '@/lib/breadcrumb/resolve-breadcrumb-merge-home';
import { findPrimaryCategoryNode, resolvePrimaryCategoryEntryUrl } from '@/lib/primary-shopping-category';
import { readPrimaryShoppingCategoryPath } from '@/lib/primary-shopping-category.server';
import EatalyCategoryPlpHeader from '@/components/eataly-category-plp-header';
import { parsePaginationPageParam } from '@/lib/pagination-utils';
import { buildPagePaginationRelUrls } from '@/utils/pagination-seo';
import { isLeafCategory } from '@/lib/category-link-target';
import SeoLinkingWrapper from '@/components/seo-linking/seo-linking-wrapper';
import { BrandDeferred } from '@/components/brand/brand-deferred';
import { useMediaQuery } from '@/hooks/use-media-query';
import { fetchBrandContent, getBrandIdFromCategory, type BrandContent } from '@/lib/api/fetch-brand';
import { SupplyChainDeferred } from '@/components/supply-chain/supply-chain-deferred';
import {
    fetchSupplyChainContent,
    getSupplyChainIdFromCategory,
    type SupplyChainContent,
} from '@/lib/api/fetch-supply-chain';
import { parseCgidFromRefine } from '@/lib/refine-cgid';
import EatalyCategoryPlpStickyBar from '@/components/eataly-category-plp-sticky-bar';
import EatalyCategoryPlpOrdinationDrawer from '@/components/eataly-category-plp-ordination-drawer';
import { getMaxNonCriticalSkeletonCount, getNonCriticalSkeletonCount } from '@/lib/search-product-grid';
import {
    getEventListingCriticalCount,
    getEventListingHitsLimit,
    isEventOrCourseCategory,
} from '@/lib/event-course-category';
import { findNavigationPillar, type NavigationPillar } from '@/lib/navigation-pillars';
import { getNavigationPillarRootIds } from '@/lib/navigation-pillars.server';
import { fetchEventCourseCategoryCounts } from '@/lib/api/event-course-category-counts.server';
import type { EventCourseCategoryCounts } from '@/lib/event-course-category-counts';
import { resolvePlpSubcategoryLinks } from '@/lib/plp-subcategory-links.server';
import type { SeoLink } from '@/lib/seo-linking/resolve-links';
import { getTranslation } from '@salesforce/storefront-next-runtime/i18n';
import { PillarPageRoute, type PillarPageRouteData } from '@/content-routes/pillar-page';
import {
    FullEditorialCategoryPageRoute,
    type FullEditorialCategoryPageRouteData,
} from '@/content-routes/full-editorial-category-page';
import type { RouteDataWithShoppingContext } from '@/lib/shopping-context';
import {
    logMissingCategoryPillarContentGroup,
    resolveCategoryBoundPillarContentGroup,
    resolvePlpContentGroup,
    type ContentGroupValue,
} from '@/lib/analytics/content-group';
import { categoryPillarPageAnalyticsContentGroupDefinition } from '@/lib/analytics/page-designer-content-group';
import { attachAnalytics } from '@/lib/analytics/route-analytics';
import { isMaintenanceError, preferredSettledRejection } from '@/lib/maintenance';
import type { Route } from './+types/_app.c.$';
/** Shopper Experience page type id identifying a Pillar Page (category landing, no product grid). */
export const PILLAR_PAGE_TYPE_ID = 'pillarPage';

/** Shopper Experience page type id identifying a Full Editorial Category Page (category landing, no product grid). */
export const FULL_EDITORIAL_CATEGORY_PAGE_TYPE_ID = 'fullEditorialCategoryPage';

const resolveCriticalPlpComponents = (page: Page) =>
    page.typeId === PILLAR_PAGE_TYPE_ID
        ? [{ regionId: 'pillarNavigation' }, { regionId: 'content', limit: 2 }]
        : page.typeId === FULL_EDITORIAL_CATEGORY_PAGE_TYPE_ID
          ? [{ regionId: 'content', limit: 2 }]
          : [{ regionId: 'gridPromo' }];

const FULL_EDITORIAL_CATEGORY_PAGE_CONTENT_COMPONENT_INCLUSIONS = [
    'carousels.crossSellCarousel',
    'carousels.editorialCarousel',
    'carousels.groupedCarousel',
    'carousels.squareCardCarousel',
    'content.cardListings',
    'content.cardMultiImages',
    'content.heroText',
    'content.heroBanner',
    'content.editorialArticleAsideComponent',
    'content.editorialSeoLinkingSection',
    'content.heroBanner',
    'content.heroModules',
    'content.heroTextMedia',
    'content.inspirationHeader',
    'content.mediaFullBleed',
    'content.recommendedRecipe',
    'content.recommendedCourse',
    'content.editorialTextMedia',
    'content.seoText',
    'content.editorialTextOnly',
    'content.editorialPromoBanner',
    'content.editorialPeopleBanner',
    'content.editorialStoreInfo',
    'layout.editorialViewportWrapper',
    'layout.editorialTabs',
    'content.editorialJobOpportunities',
    'content.editorialHeaderPress',
    'content.numbers',
] as const;

// @RegionDefinition componentTypeInclusions must stay literal for cartridge:generate.
void ([
    'carousels.crossSellCarousel',
    'carousels.editorialCarousel',
    'carousels.groupedCarousel',
    'carousels.squareCardCarousel',
    'content.cardListings',
    'content.cardMultiImages',
    'content.heroText',
    'content.heroBanner',
    'content.editorialArticleAsideComponent',
    'content.editorialSeoLinkingSection',
    'content.heroBanner',
    'content.heroModules',
    'content.heroTextMedia',
    'content.inspirationHeader',
    'content.mediaFullBleed',
    'content.recommendedRecipe',
    'content.recommendedCourse',
    'content.editorialTextMedia',
    'content.seoText',
    'content.editorialTextOnly',
    'content.editorialPromoBanner',
    'content.editorialPeopleBanner',
    'content.editorialStoreInfo',
    'layout.editorialViewportWrapper',
    'layout.editorialTabs',
    'content.editorialJobOpportunities',
    'content.editorialHeaderPress',
    'content.numbers',
] as const satisfies typeof FULL_EDITORIAL_CATEGORY_PAGE_CONTENT_COMPONENT_INCLUSIONS);

const PILLAR_PAGE_CONTENT_COMPONENT_INCLUSIONS = [
    'carousels.crossSellCarousel',
    'carousels.editorialCarousel',
    'carousels.groupedCarousel',
    'carousels.squareCardCarousel',
    'content.cardListings',
    'content.cardMultiImages',
    'content.heroText',
    'content.heroBanner',
    'content.editorialArticleAsideComponent',
    'content.editorialSeoLinkingSection',
    'content.heroTextMedia',
    'content.inspirationHeader',
    'content.mediaFullBleed',
    'content.recommendedRecipe',
    'content.recommendedCourse',
    'content.editorialTextMedia',
    'content.numbers',
    'layout.editorialViewportWrapper',
] as const;

// @RegionDefinition componentTypeInclusions must stay literal for cartridge:generate.
void ([
    'carousels.crossSellCarousel',
    'carousels.editorialCarousel',
    'carousels.groupedCarousel',
    'carousels.squareCardCarousel',
    'content.cardListings',
    'content.cardMultiImages',
    'content.heroText',
    'content.heroBanner',
    'content.editorialArticleAsideComponent',
    'content.editorialSeoLinkingSection',
    'content.heroTextMedia',
    'content.inspirationHeader',
    'content.mediaFullBleed',
    'content.recommendedRecipe',
    'content.recommendedCourse',
    'content.editorialTextMedia',
    'content.numbers',
    'layout.editorialViewportWrapper',
] as const satisfies typeof PILLAR_PAGE_CONTENT_COMPONENT_INCLUSIONS);

function stripLocalePrefixFromPath(path: string, siteCtx: SiteContext, config: AppConfig): string {
    const segments = path.split('/').filter(Boolean);
    const firstSegment = segments[0]?.toLowerCase();

    if (!firstSegment) {
        return path;
    }

    const localePrefixes = new Set<string>();
    const addLocalePrefix = (locale: SiteContext['locale']) => {
        localePrefixes.add(locale.id.toLowerCase());
        localePrefixes.add(locale.id.split('-')[0].toLowerCase());

        const configuredAlias = config.localeAliasMap?.[locale.id];
        if (configuredAlias) {
            localePrefixes.add(configuredAlias.toLowerCase());
        }

        if ('alias' in locale && locale.alias) {
            localePrefixes.add(locale.alias.toLowerCase());
        }
    };

    addLocalePrefix(siteCtx.locale);
    for (const locale of siteCtx.site.supportedLocales) {
        addLocalePrefix(locale);
    }

    if (!localePrefixes.has(firstSegment)) {
        return path;
    }

    const withoutLocalePrefix = segments.slice(1).join('/');
    return withoutLocalePrefix ? `/${withoutLocalePrefix}` : '/';
}

async function resolveCategoryIdFromUrlMapping(
    context: LoaderFunctionArgs['context'],
    categoryRulePath: string
): Promise<string | null> {
    const config = getConfig(context);
    const siteCtx = context.get(siteContext) as SiteContext;
    const normalizedCategoryPath = normalizeCategoryPath(categoryRulePath);

    if (!normalizedCategoryPath) {
        return null;
    }

    const urlSegment = stripLocalePrefixFromPath(normalizedCategoryPath, siteCtx, config);
    const session = await context.get(authContext).ref;
    const { organizationId } = config.commerce.api;

    if (!session?.accessToken || !organizationId) {
        return null;
    }

    const clients = createApiClients(context);
    let response: Awaited<ReturnType<typeof clients.shopperSeo.getUrlMapping>>;
    try {
        response = await clients.shopperSeo.getUrlMapping({
            params: {
                path: {
                    organizationId,
                },
                query: {
                    urlSegment,
                    siteId: siteCtx.site.id,
                },
            },
        });
    } catch (error) {
        if (error instanceof ApiError && error.status === 404) {
            return null;
        }

        if (error instanceof ApiError && isMaintenanceError(error)) {
            throw new Response(error.body.title || error.statusText || 'Maintenance', {
                status: 503,
                statusText: error.body.detail || error.statusText,
                headers: error.headers,
            });
        }

        throw error;
    }

    if (!response.data || response.data.resourceType?.toLowerCase() !== 'category') {
        return null;
    }

    return response.data.resourceId ?? null;
}

@PageType({
    name: 'Product Listing Page',
    description: 'Product listing page with product listings and personalized content',
    supportedAspectTypes: ['plp'],
})
@RegionDefinition([
    {
        id: 'theSelection',
        name: 'The Selection ',
        description: 'Region for the The Selection block',
        maxComponents: 1,
        componentTypeInclusions: ['content.theSelection'],
    },
    {
        id: 'productRecommendations',
        name: 'Product Recommendations',
        description: 'Region for the Product Recommendations PLP block',
        maxComponents: 1,
        componentTypeInclusions: ['carousels.productRecommendations'],
    },
    {
        id: 'gridPromo',
        name: 'Grid Promo',
        description: 'Optional promo banner inserted after the first product row',
        maxComponents: 1,
        componentTypeInclusions: ['content.editorialPromoBanner'],
    },
    {
        id: 'content',
        name: 'Content',
        description: 'Editorial content blocks below the product listing',
        maxComponents: 6,
        componentTypeInclusions: [
            'carousels.crossSellCarousel',
            'carousels.editorialCarousel',
            'carousels.groupedCarousel',
            'carousels.squareCardCarousel',
            'content.cardListings',
            'content.cardMultiImages',
            'content.heroText',
            'content.seoText',
            'content.heroBanner',
            'content.editorialArticleAsideComponent',
            'content.editorialSeoLinkingSection',
            'content.heroTextMedia',
            'content.inspirationHeader',
            'content.mediaFullBleed',
            'content.recommendedRecipe',
            'content.recommendedCourse',
            'content.editorialTextMedia',
            'layout.editorialViewportWrapper',
        ],
    },
])
export class ProductListingPageMetadata {}

@PageType({
    id: PILLAR_PAGE_TYPE_ID,
    name: 'Pillar Page',
    description: 'Category landing page with pillar navigation and editorial content (no product grid).',
    supportedAspectTypes: ['plp'],
})
@RegionDefinition([
    {
        id: 'pillarNavigation',
        name: 'Pillar navigation',
        description: 'Horizontal pillar switcher tabs',
        maxComponents: 1,
        componentTypeInclusions: ['navigation.pillarNavigation'],
    },
    {
        id: 'content',
        name: 'Content',
        description: 'Editorial content blocks',
        maxComponents: 10,
        componentTypeInclusions: [
            'carousels.crossSellCarousel',
            'carousels.editorialCarousel',
            'carousels.groupedCarousel',
            'carousels.squareCardCarousel',
            'content.cardListings',
            'content.cardMultiImages',
            'content.heroText',
            'content.seoText',
            'content.heroBanner',
            'content.editorialArticleAsideComponent',
            'content.editorialSeoLinkingSection',
            'content.heroTextMedia',
            'content.inspirationHeader',
            'content.mediaFullBleed',
            'content.recommendedRecipe',
            'content.recommendedCourse',
            'content.editorialTextMedia',
            'content.numbers',
            'layout.editorialViewportWrapper',
        ],
    },
])
export class PillarPageMetadata {
    @AttributeDefinition(categoryPillarPageAnalyticsContentGroupDefinition)
    analyticsContentGroup!: ContentGroupValue;
}

@PageType({
    id: FULL_EDITORIAL_CATEGORY_PAGE_TYPE_ID,
    name: 'Full Editorial Category Page',
    description:
        'Category landing page with full editorial content (no product grid). Replaces the standard PLP when assigned to a category.',
    supportedAspectTypes: ['plp'],
})
@RegionDefinition([
    {
        id: 'content',
        name: 'Content Region',
        description: 'Content area',
        maxComponents: 20,
        componentTypeInclusions: [
            'carousels.crossSellCarousel',
            'carousels.editorialCarousel',
            'carousels.groupedCarousel',
            'carousels.squareCardCarousel',
            'content.cardListings',
            'content.cardMultiImages',
            'content.heroText',
            'content.heroBanner',
            'content.editorialArticleAsideComponent',
            'content.editorialSeoLinkingSection',
            'content.heroBanner',
            'content.heroModules',
            'content.heroTextMedia',
            'content.inspirationHeader',
            'content.mediaFullBleed',
            'content.recommendedRecipe',
            'content.recommendedCourse',
            'content.editorialTextMedia',
            'content.seoText',
            'content.editorialTextOnly',
            'content.editorialPromoBanner',
            'content.editorialPeopleBanner',
            'content.editorialStoreInfo',
            'layout.editorialViewportWrapper',
            'layout.editorialTabs',
            'content.editorialJobOpportunities',
            'content.editorialHeaderPress',
            'content.numbers',
        ],
    },
])
export class FullEditorialCategoryPageMetadata {
    @AttributeDefinition(categoryPillarPageAnalyticsContentGroupDefinition)
    analyticsContentGroup!: ContentGroupValue;
}

type CategoryPlpPageData = RouteDataWithShoppingContext & {
    kind: 'plp';
    category: ShopperProducts.schemas['Category'];
    breadcrumbItems: EatalyBreadcrumbItem[];
    /** Breadcrumb strip background (Eataly default layout). */
    breadcrumbBackgroundClass: string;
    /** Pillar entry path for the home icon (cookie or current category). */
    breadcrumbHomeTo?: string;
    /** When true, home icon is merged onto the first trail segment. */
    breadcrumbMergeHomeWithFirstItem?: boolean;
    /** Parent category (levels 1) for SEO sibling links when the current category is a leaf. */
    parentCategoryForSeo: Promise<ShopperProducts.schemas['Category'] | undefined>;
    /**
     * Subcategory / sibling tags with product-presence filtering and shortname labels.
     * Deferred — does not block the critical PLP payload.
     */
    subcategoryLinks: Promise<SeoLink[] | null>;
    searchResultCritical: ShopperSearch.schemas['ProductSearchResult'];
    searchResultNonCritical: Promise<ShopperSearch.schemas['ProductSearchResult']>;
    /** When true, ProductGrid uses the Corsi/Eventi vertical-event tile layout. */
    useEventListingLayout: boolean;
    page: PageWithComponentData | Promise<PageWithComponentData | null> | null;
    categoryId: string;
    pageUrl: string;
    refine: string[];
    currency: string;
    locale: string;
    initialFiltersOpen?: boolean;
    initialOrdinationOpen?: boolean;
    categorySchema: Promise<ReturnType<typeof generateCategorySchema> | null>;
    /** Absolute URLs for `<link rel="prev|next">` (PLP `page` query param) */
    paginationPrevUrl: string | null;
    paginationNextUrl: string | null;
    /** 1-based listing page from the `page` query param */
    currentPage: number;
    /** Localized site name for title suffix and `og:site_name`. */
    seoSiteName: string;
    /** Brand custom object for inline Brand block (deferred; resolve with Suspense in BrandDeferred) */
    brandContent: Promise<BrandContent | null> | null;
    /** Supply chain custom object for inline SupplyChain block (deferred) */
    supplyChainContent: Promise<SupplyChainContent | null> | null;
    primaryCategoryPath: string | null;
};

type EditorialCategoryPageData =
    | PillarPageRouteData
    | (FullEditorialCategoryPageRouteData & RouteDataWithShoppingContext);

export type CategoryRouteData = CategoryPlpPageData | EditorialCategoryPageData;

type EditorialCategoryPageKind = 'pillarPage' | 'fullEditorialCategoryPage';

function hasListingQueryParams(searchParams: URLSearchParams): boolean {
    if (searchParams.has(PRODUCT_SEARCH_QUERY_PARAMS.PAGE)) {
        return true;
    }
    if (searchParams.has(PRODUCT_SEARCH_QUERY_PARAMS.SORT)) {
        return true;
    }
    return searchParams.getAll(PRODUCT_SEARCH_QUERY_PARAMS.REFINE).length > 0;
}

async function loadEditorialCategoryPage(
    args: LoaderFunctionArgs,
    categoryId: string,
    requestUrl: URL,
    categoryData: ShopperProducts.schemas['Category'],
    navigationPillar: NavigationPillar | undefined,
    page: PageWithComponentData,
    kind: EditorialCategoryPageKind
): Promise<EditorialCategoryPageData> {
    const { context } = args;
    const logger = getLogger(context);

    if (kind === 'fullEditorialCategoryPage' && hasListingQueryParams(requestUrl.searchParams)) {
        throw await notFoundResponse(args);
    }

    const publicOrigin = getPublicOrigin(args.request);
    const pageUrl = buildCanonicalUrl(publicOrigin, requestUrl.pathname, '');
    const primaryCategoryPath = resolvePrimaryCategoryEntryUrl(categoryData);
    const { t } = getTranslation(context);
    const seoSiteName = t(DEFAULT_SITE_NAME_KEY, { ns: 'common' });

    const schemaSite = getSchemaSite(args);
    const structuredData = generatePageDesignerLists(
        kind === 'pillarPage'
            ? {
                  ...page,
                  regions: page.regions?.filter((region) => ['pillarNavigation', 'content'].includes(region.id)),
              }
            : page,
        pageUrl,
        schemaSite,
        (path) => buildUrlFromContext(path, context)
    )
        .then((lists) =>
            generateContentSchema({
                type: 'CollectionPage',
                page,
                title: categoryData.name || categoryId,
                description: categoryData.pageDescription,
                pageUrl,
                breadcrumbItems: buildEatalyCategoryBreadcrumbItems(categoryData),
                site: schemaSite,
                lists,
            })
        )
        .catch((error: unknown) => {
            logger.error('Category: failed to generate editorial structured data', { error, categoryId });
            return null;
        });
    const commonData = {
        structuredData,
        category: categoryData,
        page,
        categoryId,
        pageUrl,
        breadcrumbItems: [],
        breadcrumbBackgroundClass: 'bg-neutral-white',
        primaryCategoryPath,
        shoppingContext: {
            primaryCategoryPath,
            navigationPillar,
        },
        seoSiteName,
    };

    const contentGroup = resolveCategoryBoundPillarContentGroup({
        page,
        categoryId,
        pillar: navigationPillar,
        kind,
        pageUrl,
        onMissingAttribute: (details) => logMissingCategoryPillarContentGroup(logger, details),
    });

    let eventCourseCategoryCounts: EventCourseCategoryCounts | undefined;
    if (kind === 'pillarPage' && (navigationPillar === 'events' || navigationPillar === 'courses')) {
        eventCourseCategoryCounts = await fetchEventCourseCategoryCounts(context);
    }

    return kind === 'pillarPage'
        ? attachAnalytics({ ...commonData, kind: 'pillarPage', eventCourseCategoryCounts }, contentGroup)
        : attachAnalytics({ ...commonData, kind: 'fullEditorialCategoryPage' }, contentGroup);
}

/**
 * Category data and the Page Designer page are both critical for the category route: a failure has to
 * surface all available information from the underlying `ApiError`, falling back to a generic 500.
 * `fetchCategory` wraps failures as `NormalizedApiError`; `fetchPageWithComponentData` throws
 * `Response(503)` on maintenance. Preserve those statuses so middleware can redirect.
 */
function documentStatusForCriticalCategoryError(status: number): number {
    // 502 is not a maintenance signal; returning it as the document status becomes MRT Bad Gateway.
    return status === 502 ? 500 : status;
}

function rethrowCriticalCategoryPageError(error: unknown): never {
    if (error instanceof Response) {
        if (error.status === 502 && !isMaintenanceError(error)) {
            throw new Response('Internal Server Error', { status: 500 });
        }
        throw error;
    }
    if (error instanceof ApiError) {
        throw new Response(error.body.title || error.statusText, {
            status: documentStatusForCriticalCategoryError(error.status),
            statusText: error.body.detail || error.statusText,
            headers: error.headers,
        });
    }
    if (error instanceof NormalizedApiError && error.status) {
        const status = documentStatusForCriticalCategoryError(error.status);
        const cause = error.cause;
        if (cause instanceof ApiError) {
            throw new Response(cause.body.title || cause.statusText, {
                status,
                statusText: cause.body.detail || cause.statusText,
                headers: cause.headers,
            });
        }
        throw new Response(error.message, {
            status,
            statusText: error.message,
        });
    }
    if (isMaintenanceError(error)) {
        throw error;
    }
    throw new Response('Internal Server Error', { status: 500 });
}

/**
 * Server-side loader function that fetches category data and product search results.
 * This function runs on the server during SSR and prepares data for the category page.
 * @returns Object containing search results, category data, and page metadata
 */
export async function loader(args: LoaderFunctionArgs): Promise<CategoryRouteData> {
    const { context, request } = args;
    const requestUrl = new URL(request.url);
    const categoryId = await resolveCategoryIdFromUrlMapping(context, requestUrl.pathname);

    if (!categoryId) {
        throw await notFoundResponse(args);
    }

    const { searchParams } = requestUrl;
    if (searchParams.has(PRODUCT_SEARCH_QUERY_PARAMS.PAGE)) {
        const malformedPageParam = getQueryParam(searchParams, PRODUCT_SEARCH_QUERY_PARAMS.PAGE);
        if (parsePaginationPageParam(malformedPageParam) === null) {
            throw await notFoundResponse(args);
        }
    }

    const logger = getLogger(context);
    const siteCtx = context.get(siteContext);
    if (!siteCtx) {
        logger.error('Category: site context is not available');
        throw new Response('Site context is not available', { status: 500 });
    }

    const pageParam = getQueryParam(searchParams, PRODUCT_SEARCH_QUERY_PARAMS.PAGE);
    const hasPageParam = searchParams.has(PRODUCT_SEARCH_QUERY_PARAMS.PAGE);
    const currentPage = parsePaginationPageParam(pageParam);

    if (currentPage === null) {
        throw await notFoundResponse(args);
    }

    logger.debug('Category: loader starting', {
        categoryId,
        currentPage,
    });
    const config = getConfig(context);
    const sort = getQueryParam(searchParams, PRODUCT_SEARCH_QUERY_PARAMS.SORT);
    const refine = getAllQueryParams(searchParams, PRODUCT_SEARCH_QUERY_PARAMS.REFINE);
    const initialFiltersOpen = getInitialFiltersOpen(searchParams);
    const initialOrdinationOpen = getInitialOrdinationOpen(searchParams);

    const { currency } = siteCtx;
    const locale = siteCtx.locale.id;

    // Keep non-category refinements and apply exactly one category refinement.
    // If URL already contains a cgid refine (e.g. from quick filters), honor it.
    // Otherwise, default to the category id from the route path.
    const refinedCgidId = parseCgidFromRefine(refine);
    const userRefine = refine.filter((r) => !r.startsWith('cgid='));
    userRefine.push(`cgid=${refinedCgidId ?? categoryId}`);

    const hitsConfig = config.search.products.hits;

    // Fetch the category once with enough depth for both pillar routing and PLP quick filters.
    // Page Designer is independent, so start it alongside the category request.
    // allSettled observes both rejections so a second 503 cannot become an unhandledRejection.
    const [categoryResult, pageResult] = await Promise.allSettled([
        fetchCategory(context, categoryId, 2),
        fetchPageWithComponentData(
            args,
            {
                aspectType: 'plp',
                categoryId,
            },
            { criticalComponents: resolveCriticalPlpComponents }
        ),
    ]);
    const criticalRejections = [categoryResult, pageResult].filter(
        (result): result is PromiseRejectedResult => result.status === 'rejected'
    );
    if (criticalRejections.length > 0) {
        rethrowCriticalCategoryPageError(preferredSettledRejection(criticalRejections));
    }
    const categoryData = (categoryResult as PromiseFulfilledResult<ShopperProducts.schemas['Category']>).value;
    const resolvedPage = (pageResult as PromiseFulfilledResult<PageWithComponentData | null>).value;
    const navigationPillar = findNavigationPillar(
        await getNavigationPillarRootIds(context),
        findPrimaryCategoryNode(categoryData)?.id
    );

    if (resolvedPage?.typeId === PILLAR_PAGE_TYPE_ID) {
        return loadEditorialCategoryPage(
            args,
            categoryId,
            requestUrl,
            categoryData,
            navigationPillar,
            resolvedPage,
            'pillarPage'
        );
    }

    if (resolvedPage?.typeId === FULL_EDITORIAL_CATEGORY_PAGE_TYPE_ID) {
        return loadEditorialCategoryPage(
            args,
            categoryId,
            requestUrl,
            categoryData,
            navigationPillar,
            resolvedPage,
            'fullEditorialCategoryPage'
        );
    }

    const parentCategoryForSeoPromise =
        isLeafCategory(categoryData) && categoryData.parentCategoryId
            ? fetchCategory(context, categoryData.parentCategoryId, 1).catch(() => undefined)
            : Promise.resolve(undefined);

    // Header tags are replaced by the category filter. Skip the per-candidate product searches.
    // The parent category fetch stays: SEO "Potrebbero interessarti" still needs siblings on leaf PLPs.
    const subcategoryLinksPromise =
        categoryData.c_showCategoryFilter === true
            ? Promise.resolve(null)
            : parentCategoryForSeoPromise.then((parentForSiblings) =>
                  resolvePlpSubcategoryLinks(context, categoryData, parentForSiblings)
              );

    const useEventListingLayout = isEventOrCourseCategory(categoryData);
    const limit = getEventListingHitsLimit(hitsConfig, useEventListingLayout);
    const offset = (currentPage - 1) * limit;
    const criticalCount = getEventListingCriticalCount(hitsConfig, useEventListingLayout);
    const safeCriticalCount = Math.min(criticalCount, limit);

    const searchResultCritical = await fetchSearchProducts(context, {
        limit: safeCriticalCount,
        offset,
        ...(sort ? { sort } : {}),
        refine: userRefine,
        currency,
    });
    const effectiveCriticalCount = searchResultCritical.hits?.length ?? 0;
    const resultsTotal = searchResultCritical.total ?? 0;
    const searchResultNonCritical = fetchSearchProducts(context, {
        limit: limit - effectiveCriticalCount,
        offset: offset + effectiveCriticalCount,
        ...(sort ? { sort } : {}),
        refine: userRefine,
        currency,
    });

    const total = resultsTotal;
    const totalPages = Math.ceil(total / limit);
    const maxValidPage = Math.max(totalPages, 1);

    if (currentPage > maxValidPage) {
        throw await notFoundResponse(args);
    }

    if (currentPage === 1 && hasPageParam) {
        const params = new URLSearchParams(requestUrl.search);
        params.delete(PRODUCT_SEARCH_QUERY_PARAMS.PAGE);
        const newSearch = params.toString();
        const redirectUrl = `${requestUrl.pathname}${newSearch ? `?${newSearch}` : ''}`;
        throw redirect(redirectUrl);
    }

    const publicOrigin = getPublicOrigin(request);
    const pageUrl = buildCanonicalUrl(publicOrigin, requestUrl.pathname, requestUrl.search);
    const { prev: paginationPrevUrl, next: paginationNextUrl } = buildPagePaginationRelUrls({
        origin: publicOrigin,
        pathname: requestUrl.pathname,
        search: requestUrl.search,
        limit,
        offset,
        total: resultsTotal,
    });

    // Generate category schema in loader (server-side) for SEO
    const categorySchemaPromise = searchResultNonCritical
        .then((searchResult: ShopperSearch.schemas['ProductSearchResult']) => {
            try {
                // Validate inputs before generating schema
                if (!categoryData || !searchResult) {
                    return null;
                }
                return generateCategorySchema({
                    category: categoryData,
                    searchResult: {
                        ...searchResult,
                        hits: [...(searchResultCritical.hits || []), ...(searchResult.hits || [])],
                        total: resultsTotal,
                        offset: searchResultCritical.offset,
                    },
                    config,
                    pageUrl,
                    defaultCurrency: currency,
                    site: getSchemaSite(args),
                });
            } catch {
                return null;
            }
        })
        .catch(() => {
            return null;
        });

    const breadcrumbItems = buildEatalyCategoryBreadcrumbItems(categoryData);
    const subCategoryForRefine =
        refinedCgidId != null && refinedCgidId !== categoryData.id
            ? categoryData.categories?.find((c) => c.id === refinedCgidId)
            : undefined;
    const categoryForEditorial = subCategoryForRefine ?? categoryData;
    const brandKey = getBrandIdFromCategory(categoryForEditorial);
    const supplyChainKey = getSupplyChainIdFromCategory(categoryForEditorial);
    const brandContent = brandKey ? fetchBrandContent(context, brandKey) : null;
    const supplyChainContent = supplyChainKey ? fetchSupplyChainContent(context, supplyChainKey) : null;
    const primaryCategoryPath = resolvePrimaryCategoryEntryUrl(categoryData);
    const primaryCategoryPathFromCookie = await readPrimaryShoppingCategoryPath(args.request, args.context);
    const breadcrumbHomeTo = primaryCategoryPathFromCookie ?? primaryCategoryPath ?? undefined;
    const breadcrumbMergeHomeWithFirstItem = resolveBreadcrumbMergeHomeWithFirstItem({
        items: breadcrumbItems,
        homeTo: primaryCategoryPath ?? breadcrumbHomeTo,
        currentPathname: requestUrl.pathname,
    });
    const { t } = getTranslation(context);
    const seoSiteName = t(DEFAULT_SITE_NAME_KEY, { ns: 'common' });

    return attachAnalytics(
        {
            kind: 'plp',
            category: categoryData,
            parentCategoryForSeo: parentCategoryForSeoPromise,
            subcategoryLinks: subcategoryLinksPromise,
            searchResultCritical,
            searchResultNonCritical,
            useEventListingLayout,
            page: resolvedPage,
            categoryId,
            pageUrl,
            refine: userRefine,
            breadcrumbItems,
            breadcrumbBackgroundClass: 'bg-neutral-white',
            breadcrumbHomeTo,
            breadcrumbMergeHomeWithFirstItem,
            currency,
            locale,
            initialFiltersOpen,
            initialOrdinationOpen,
            categorySchema: categorySchemaPromise,
            paginationPrevUrl,
            paginationNextUrl,
            currentPage,
            seoSiteName,
            brandContent,
            supplyChainContent,
            primaryCategoryPath,
            shoppingContext: {
                primaryCategoryPath,
                navigationPillar,
            },
        },
        resolvePlpContentGroup({
            pillar: navigationPillar,
            useEventListingLayout,
            kind: 'plp',
        })
    );
}
export function shouldRevalidate(args: ShouldRevalidateFunctionArgs) {
    if (shouldSkipRevalidationForCartOrWishlistAndNewsletterActions(args)) {
        return false;
    }

    const { currentUrl, nextUrl, defaultShouldRevalidate } = args;
    const clientOnlyParamsChanged =
        currentUrl.pathname === nextUrl.pathname &&
        currentUrl.search !== nextUrl.search &&
        getSearchWithoutClientOnlyParams(currentUrl.search) === getSearchWithoutClientOnlyParams(nextUrl.search);

    if (clientOnlyParamsChanged) {
        return false;
    }

    return defaultShouldRevalidate;
}

export const meta: Route.MetaFunction = ({ loaderData, location, error }) => {
    if (!loaderData || error) return [];

    const editorialPage =
        loaderData.kind === 'pillarPage' || loaderData.kind === 'fullEditorialCategoryPage'
            ? loaderData.page
            : undefined;
    const { title, description, rawTitle } = resolvePageSeo({
        pageMetaTags: loaderData.kind === 'plp' ? loaderData.searchResultCritical.pageMetaTags : undefined,
        pageTitle: loaderData.category.pageTitle || editorialPage?.pageTitle,
        pageDescription: loaderData.category.pageDescription || editorialPage?.pageDescription,
        title: editorialPage?.name || loaderData.category.name || loaderData.category.id,
        description: editorialPage?.description || loaderData.category.description || undefined,
    });
    const pageParam = new URLSearchParams(location.search).get(PRODUCT_SEARCH_QUERY_PARAMS.PAGE);
    const page = loaderData.kind === 'plp' ? (parsePaginationPageParam(pageParam) ?? loaderData.currentPage) : 1;
    const robots = page > 1 ? 'noindex, follow' : 'index, follow';
    const socialTitle = title || loaderData.seoSiteName;

    const descriptors: MetaDescriptor[] = [
        { title: rawTitle ? title : `${title} | ${loaderData.seoSiteName}` },
        { name: 'robots', content: robots },
        { property: 'og:title', content: socialTitle },
        { property: 'og:type', content: 'website' },
        { property: 'og:url', content: loaderData.pageUrl },
        { property: 'og:site_name', content: loaderData.seoSiteName },
        { name: 'twitter:card', content: 'summary' },
        { name: 'twitter:title', content: socialTitle },
    ];

    if (description) {
        descriptors.push(
            { name: 'description', content: description },
            { property: 'og:description', content: description },
            { name: 'twitter:description', content: description }
        );
    }

    if (loaderData.kind === 'plp' && loaderData.paginationPrevUrl) {
        descriptors.push({ tagName: 'link', rel: 'prev', href: loaderData.paginationPrevUrl });
    }
    if (loaderData.kind === 'plp' && loaderData.paginationNextUrl) {
        descriptors.push({ tagName: 'link', rel: 'next', href: loaderData.paginationNextUrl });
    }
    if (loaderData.pageUrl) {
        descriptors.push({ tagName: 'link', rel: 'canonical', href: loaderData.pageUrl });
    }

    return descriptors;
};

/**
 * Category page component that displays a product category with filtering, sorting, and pagination.
 * This component uses the createPage factory to handle Suspense patterns.
 * @returns JSX element representing the category page
 */
/**
 * Component that renders JSON-LD schema when categorySchema promise resolves.
 * Must be inside Suspense boundary to ensure it streams correctly in SSR.
 */
function CategoryJsonLd({
    categorySchemaPromise,
}: {
    categorySchemaPromise: Promise<ReturnType<typeof generateCategorySchema> | null>;
}) {
    const categorySchema = use(categorySchemaPromise);
    return categorySchema ? <JsonLd data={categorySchema} id="category-schema" /> : null;
}

export default function CategoryRoute({ loaderData }: { loaderData: CategoryRouteData }) {
    if (loaderData.kind === 'pillarPage') {
        return <PillarPageRoute key={loaderData.categoryId} data={loaderData} />;
    }

    if (loaderData.kind === 'fullEditorialCategoryPage') {
        return <FullEditorialCategoryPageRoute key={loaderData.categoryId} data={loaderData} />;
    }

    return <CategoryPlpPage loaderData={loaderData} />;
}

function CategoryPlpPage({
    loaderData: {
        category,
        parentCategoryForSeo,
        subcategoryLinks,
        searchResultCritical,
        searchResultNonCritical,
        useEventListingLayout,
        page,
        categoryId,
        refine,
        locale,
        currency,
        initialFiltersOpen,
        initialOrdinationOpen,
        categorySchema,
        currentPage,
        supplyChainContent,
        brandContent,
    },
}: {
    loaderData: CategoryPlpPageData;
}) {
    const config = useConfig();

    const { filtersOpen, handleFiltersDrawerOpenChange } = useFiltersPanelState(initialFiltersOpen);
    const [ordinationOpen, toggleOrdinationOpen, handleOrdinationOpenChange] =
        useOrdinationPanelState(initialOrdinationOpen);
    const [pageNumber, setPageNumber] = useState(currentPage);

    // Determine the maximum number of skeletons to display in the product grid
    // Out-of-the-box the idea is to not display more than 8 skeletons, i.e., two rows on a desktop device.
    // Wrap in Math.max(0, ...) to prevent negative values when criticalCount is high(er).
    const isDesktop = useMediaQuery('(min-width: 1024px)');
    const hitsConfig = config.search.products.hits;
    const limit = getEventListingHitsLimit(hitsConfig, Boolean(useEventListingLayout));
    const maxSkeletonCount = getMaxNonCriticalSkeletonCount(hitsConfig, isDesktop, Boolean(useEventListingLayout));
    const criticalCount = searchResultCritical.hits?.length ?? 0;
    const resolvedNonCriticalCount = getNonCriticalSkeletonCount({
        maxSkeletons: maxSkeletonCount,
        limit,
        criticalCount,
        total: searchResultCritical.total,
        offset: searchResultCritical.offset,
    });

    const analytics = useAnalytics();
    const lastTrackedDataRef = useRef<string | null>(null);

    const location = useLocation();
    const navigation = useNavigation();
    const searchWithoutPlpPanelUi = useMemo(() => getSearchWithoutClientOnlyParams(location.search), [location.search]);
    const pageIdentity = `${categoryId}-${currency}-${locale}`;
    const analyticsKey = `${pageIdentity}-${searchWithoutPlpPanelUi}-${location.hash}`;
    const productGridDataKey = `${pageIdentity}-${searchWithoutPlpPanelUi}`;
    const listingTopRef = useRef<HTMLDivElement>(null);
    useListingPaginationScroll(listingTopRef);
    const isProductGridLoading = useMemo(() => {
        if (navigation.state === 'idle' || !navigation.location) {
            return false;
        }
        const pathnameChanged = navigation.location.pathname !== location.pathname;
        if (pathnameChanged) {
            // Category PLP → category PLP (e.g. /c/spesa-online → /c/bibite). Skip PDP and other routes.
            return isCategoryPlpPathname(location.pathname) && isCategoryPlpPathname(navigation.location.pathname);
        }
        return isSamePathnameListingDataNavigationPending({
            pathname: location.pathname,
            search: location.search,
            navigationState: navigation.state,
            navigationLocation: navigation.location,
        });
    }, [location.pathname, location.search, navigation.location, navigation.state]);

    const nonCriticalCount = resolvedNonCriticalCount;

    const nonCriticalPromise = useMemo(
        () => searchResultNonCritical.then((r) => r.hits ?? []),
        [searchResultNonCritical]
    );

    const [, startTransition] = useTransition();

    useEffect(() => {
        // Only track if we haven't already tracked this specific data combination
        if (analyticsKey !== lastTrackedDataRef.current) {
            lastTrackedDataRef.current = analyticsKey;

            startTransition(() => {
                void nonCriticalPromise
                    .then((searchHitsData: ShopperSearch.schemas['ProductSearchHit'][]) => {
                        if (analytics) {
                            void analytics.trackViewCategory({
                                category,
                                searchResults: [...(searchResultCritical.hits ?? []), ...searchHitsData],
                                sort:
                                    searchResultCritical.selectedSortingOption ||
                                    searchResultCritical.sortingOptions?.[0]?.label ||
                                    '',
                                refinements: searchResultCritical.selectedRefinements ?? {},
                            });
                        }
                    })
                    .catch(() => {
                        // Silently handle promise rejection
                    });
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [analytics, category, analyticsKey, nonCriticalPromise]);

    const handleProductClick = useCallback(
        (product: ShopperSearch.schemas['ProductSearchHit']) => {
            if (analytics) {
                void analytics.trackClickProductInCategory({
                    category,
                    product,
                });
            }
        },
        [analytics, category]
    );

    return (
        <div className="relative" ref={listingTopRef}>
            <EatalyCategoryPlpHeader
                category={category}
                subcategoryLinksPromise={subcategoryLinks}
                isNavigating={isProductGridLoading}
            />
            <EatalyCategoryPlpStickyBar
                result={searchResultCritical}
                refine={refine}
                filtersOpen={filtersOpen}
                onFiltersDrawerOpenChange={handleFiltersDrawerOpenChange}
                ordinationOpen={ordinationOpen}
                onToggleOrdination={toggleOrdinationOpen}
                onOrdinationOpenChange={handleOrdinationOpenChange}
                promotePriceFilter={useEventListingLayout}
                isListingDataPending={isProductGridLoading}
                paginationLimit={limit}
                pageNumber={pageNumber}
                categoryId={category.id}
                showCategoryFilter={category.c_showCategoryFilter === true}
            />
            <EatalyCategoryPlpOrdinationDrawer
                open={ordinationOpen}
                onOpenChange={handleOrdinationOpenChange}
                result={searchResultCritical}
            />
            <UITarget targetId="sfcc.plp.search.summary" />
            <div className="pt-6 md:pt-10 pb-16 md:pb-20 min-h-[calc(100vh-300px)] flex flex-col">
                <UITarget targetId="sfcc.plp.agent.categoryHelper" />
                <UITarget targetId="sfcc.plp.search.results">
                    <ProductGrid
                        key={productGridDataKey}
                        critical={searchResultCritical.hits ?? []}
                        nonCritical={nonCriticalPromise}
                        nonCriticalCount={nonCriticalCount}
                        maxSkeletonCount={maxSkeletonCount}
                        hasRefinementsPanel={filtersOpen}
                        handleProductClick={handleProductClick}
                        isLoading={isProductGridLoading}
                        tileLayout={useEventListingLayout ? 'vertical-event' : 'horizontal'}
                        mobileGridColumns={useEventListingLayout ? 2 : 1}
                        interstitial={<Region page={page} regionId="gridPromo" />}
                    />
                </UITarget>
                <CustomPagination
                    limit={limit}
                    currentPage={currentPage}
                    total={searchResultCritical.total}
                    setPageNumber={setPageNumber}
                />
                {/* theSelection */}
                <Region page={page} regionId="theSelection" />
            </div>

            {brandContent && <BrandDeferred contentPromise={brandContent} />}
            {supplyChainContent && <SupplyChainDeferred contentPromise={supplyChainContent} />}
            {/* productRecommendationsPLP */}
            <Region page={page} regionId="productRecommendations" />
            <Region page={page} regionId="content" />

            <Suspense fallback={null}>
                <Await resolve={parentCategoryForSeo}>
                    {(resolvedParentCategoryForSeo) => (
                        <SeoLinkingWrapper
                            refine={refine}
                            current={category}
                            parentForSiblings={resolvedParentCategoryForSeo}
                        />
                    )}
                </Await>
            </Suspense>
            <Suspense fallback={null}>
                <CategoryJsonLd categorySchemaPromise={categorySchema} />
            </Suspense>
        </div>
    );
}
